import React, {
  useState,
  useRef,
  useEffect,
  type PropsWithChildren,
  useLayoutEffect,
} from 'react';
import {
  type EmbeddedWhiteboardElement,
  Resizer,
  useDetectElementSize,
  SelectionRectangle,
  useSlateStatic,
} from '@aha-app/aha-editor-v2';
import WhiteboardContainer from 'components/whiteboards/WhiteboardContainer';
import WhiteboardContainerController from 'components/whiteboards/WhiteboardContainerController';
import { type RecordId } from 'javascripts/shared-types';

interface EmbeddedWhiteboardComponentProps {
  attributes?: any;
  readOnly?: any;
  toolbarVisible?: boolean;
  defaultProject?: { id: RecordId; name: string };
  sharingOptions?: any;
  commentsEnabled?: boolean;
  canFacilitate: boolean;
  collaborative: boolean;
  element: EmbeddedWhiteboardElement;
  containingPageId?: RecordId;
  setController?: (controller: WhiteboardContainerController | null) => void;
}

type WhiteboardData = Record<string, unknown>;
type AttachmentUrls = Record<RecordId, string>;

const DEFAULT_DIMENSIONS = {
  resizer_width: 540,
  resizer_height: 300,
  resizer_originalWidth: 2048,
  resizer_originalHeight: 2048 / (16 / 9),
};

const EmbeddedWhiteboardComponent: React.FC<
  PropsWithChildren<EmbeddedWhiteboardComponentProps>
> = ({
  attributes,
  element,
  readOnly,
  toolbarVisible,
  defaultProject,
  sharingOptions,
  commentsEnabled,
  canFacilitate,
  collaborative,
  children,
  containingPageId,
  setController,
}) => {
  const controllerRef = useRef<WhiteboardContainerController>(null);
  const whiteboardContent = useRef<WhiteboardData>();
  const whiteboardAttachmentUrls = useRef<AttachmentUrls>();
  const [shouldRender, setShouldRender] = useState(collaborative);
  const editor = useSlateStatic();
  const elementWithDefaultDimensions = {
    ...DEFAULT_DIMENSIONS,
    ...element,
  };

  const { width, height, originalWidth, originalHeight } = useDetectElementSize(
    elementWithDefaultDimensions
  );

  const onResize = () => {
    if (controllerRef.current) {
      controllerRef.current.actionZoomIntoView();
    }
  };

  useLayoutEffect(() => {
    if (setController) {
      setController(controllerRef.current);
    }

    return () => {
      if (setController) {
        setController(null);
      }
    };
  });

  useLayoutEffect(() => {
    if (controllerRef.current && toolbarVisible) {
      void controllerRef.current?.actionUpdateUnresolvedCommentCount();
    }
  }, [toolbarVisible]);

  useEffect(() => {
    const whiteboardId = element.whiteboard_id;

    if (whiteboardId && !collaborative) {
      const domElement = document.getElementById(
        `whiteboard_json_${whiteboardId}`
      );
      if (domElement && domElement.dataset.json) {
        whiteboardContent.current = JSON.parse(
          domElement.dataset.json
        ) as WhiteboardData;
        if (domElement.dataset.attachmentUrls) {
          whiteboardAttachmentUrls.current = JSON.parse(
            domElement.dataset.attachmentUrls
          ) as AttachmentUrls;
        }
        setShouldRender(true);
      }
    }
  }, [element, collaborative]);

  const editable = readOnly
    ? WhiteboardContainerController.EDITING_READ_ONLY
    : WhiteboardContainerController.EDITING_EDITABLE_IN_FULL_SCREEN;

  if (!shouldRender) return null;

  const canEdit = collaborative;

  return (
    <Resizer
      width={width}
      height={height}
      originalWidth={originalWidth}
      originalHeight={originalHeight}
      element={element}
      handles={['se']}
      keepRatio={false}
      onResized={() => onResize()}
      showPixelSize
      liveResize
    >
      <SelectionRectangle fill>
        <div
          {...attributes}
          style={{ width: '100%', height: '100%' }}
          contentEditable={false}
        >
          <WhiteboardContainer
            controllerRef={controllerRef}
            dimensions={'container'}
            editable={editable}
            whiteboardId={element.whiteboard_id}
            initialVersion={null} // For load from collaboration server for now.
            initialData={whiteboardContent.current}
            attachmentUrls={whiteboardAttachmentUrls.current}
            editor={canEdit ? editor : null}
            node={canEdit ? element : null}
            editorV2
            showContainerHeader={false}
            commentsEnabled={commentsEnabled}
            defaultProject={defaultProject}
            sharingOptions={sharingOptions}
            zendeskIconVisible={false}
            canFacilitate={canFacilitate}
            collaborative={collaborative}
            allowHistory={collaborative && !readOnly}
            containingPageId={containingPageId}
          />
          {children}
        </div>
      </SelectionRectangle>
    </Resizer>
  );
};

export default EmbeddedWhiteboardComponent;
